import React, { useCallback, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import moment from "moment";
import "react-datepicker/dist/react-datepicker.css";

import { Alert, Button, Card, Col, Row, Tabs, Typography } from "antd";

import useDashboardProtoData from "./useDashboardProtoData";
import DashboardProtoTable from "./DashboardProtoTable";
import DashboardProtoChart from "./DashboardProtoChart";
import { DASHBOARD_TABS } from "./dashboardProtoFormat";

const { Title, Text } = Typography;

function TabContent({ tabKey, tables, charts, loading, dateStartStr, dateEndStr }) {
  const tabTables = (tables && tables[tabKey]) || {};
  const tabCharts = (charts && charts[tabKey]) || {};
  const hasMethod = Boolean(tabCharts.method);
  const hasPlan = Boolean(tabCharts.plan);
  const chartSpan = hasMethod && hasPlan ? 12 : 24;

  return (
    <div>
      {tabTables.general ? (
        <DashboardProtoTable
          table={tabTables.general}
          loading={loading}
          filename={`${tabKey}_geral_${dateStartStr}_${dateEndStr}.csv`}
        />
      ) : null}
      {tabTables.recurrence ? (
        <DashboardProtoTable
          table={tabTables.recurrence}
          loading={loading}
          filename={`${tabKey}_recorrencia_${dateStartStr}_${dateEndStr}.csv`}
        />
      ) : null}
      <Row gutter={[16, 16]}>
        {hasMethod ? (
          <Col xs={24} md={chartSpan}>
            <DashboardProtoChart chart={tabCharts.method} loading={loading} />
          </Col>
        ) : null}
        {hasPlan ? (
          <Col xs={24} md={chartSpan}>
            <DashboardProtoChart chart={tabCharts.plan} loading={loading} />
          </Col>
        ) : null}
        {tabCharts.overview ? (
          <Col span={24}>
            <DashboardProtoChart chart={tabCharts.overview} loading={loading} />
          </Col>
        ) : null}
      </Row>
    </div>
  );
}

export default function DashboardProto() {
  const [hasLoaded, setHasLoaded] = useState(false);

  const { mode, viewModel, isLoading, error, load } = useDashboardProtoData({
    mode: "api",
    noAuth: true,
    endpoint: "https://5ycmireyrypaqq2o6iofc2bffm0xqlkg.lambda-url.us-east-1.on.aws/",
  });
  const [startDate, setStartDate] = useState(() => new Date());
  const [endDate, setEndDate] = useState(() => new Date());

  const kpiItems = useMemo(() => {
    if (!hasLoaded) {
      return [
        { key: "faturamento", label: "Faturamento do período", value: 0, format: "brl" },
        { key: "meta", label: "Meta do período", value: 0, format: "brl" },
        { key: "mrr", label: "MRR do período", value: 0, format: "brl" },
        { key: "arr", label: "ARR do período", value: 0, format: "brl" },
      ];
    }

    const cards = viewModel?.cards || [];
    const findValue = (label) => {
      for (const card of cards) {
        const values = card?.values || {};
        if (values[label] !== undefined && values[label] !== null) return values[label];
      }
      return undefined;
    };

    return [
      { key: "faturamento", label: "Faturamento do período", value: findValue("Faturamento (R$)"), format: "brl" },
      { key: "meta", label: "Meta do período", value: findValue("Meta (R$)"), format: "brl" },
      { key: "mrr", label: "MRR do período", value: findValue("MRR (R$)"), format: "brl" },
      { key: "arr", label: "ARR do período", value: findValue("ARR (R$)"), format: "brl" },
    ].filter((item) => item.value !== undefined && item.value !== null);
  }, [hasLoaded, viewModel?.cards]);

  const formatKpiValue = useCallback((value, format) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "string") return value;
    if (typeof value === "number") {
      if (format === "brl") {
        return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
      }
      return new Intl.NumberFormat("pt-BR").format(value);
    }
    return String(value);
  }, []);

  const dateStartStr = useMemo(() => (startDate ? moment(startDate).format("YYYY-MM-DD") : ""), [startDate]);
  const dateEndStr = useMemo(() => (endDate ? moment(endDate).format("YYYY-MM-DD") : ""), [endDate]);

  const onLoad = useCallback(() => {
    setHasLoaded(true);
    load({ dateStart: dateStartStr, dateEnd: dateEndStr });
  }, [dateEndStr, dateStartStr, load]);

  const tabItems = useMemo(
    () =>
      DASHBOARD_TABS.map((tab) => ({
        key: tab.key,
        label: tab.label,
        forceRender: true,
        children: (
          <TabContent
            tabKey={tab.key}
            tables={viewModel?.tables}
            charts={viewModel?.charts}
            loading={isLoading}
            dateStartStr={dateStartStr}
            dateEndStr={dateEndStr}
          />
        ),
      })),
    [dateEndStr, dateStartStr, isLoading, viewModel?.charts, viewModel?.tables],
  );

  return (
    <div style={{ padding: 16 }}>
      <style>{`
        .dashboard-proto-tabs .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
          color: #0A4148;
        }
        .dashboard-proto-tabs .ant-tabs-ink-bar {
          background: #0A4148;
        }
        .dash-row-header {
          font-weight: 600;
        }
        .dash-row-total {
          background: #EEF4F6;
          font-weight: 600;
        }
        .dash-row-grand {
          background: #D3E2E7;
          font-weight: 700;
        }
      `}</style>
      <Row gutter={[16, 16]} align="middle">
        <Col flex="auto">
          <Title level={3} style={{ margin: 0 }}>
            Dashboard (Protótipo)
          </Title>
          <Text type="secondary">
            Modo: <b>{mode}</b> · tabelas e gráficos por período
          </Text>
        </Col>
        <Col>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              placeholderText="Data inicial"
              dateFormat="dd/MM/yyyy"
            />
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              placeholderText="Data final"
              dateFormat="dd/MM/yyyy"
            />
            <Button type="primary" onClick={onLoad} loading={isLoading}>
              Carregar dados
            </Button>
          </div>
        </Col>
      </Row>

      {error ? (
        <div style={{ marginTop: 16 }}>
          <Alert type="error" message="Erro" description={String(error)} />
        </div>
      ) : null}

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {kpiItems.map((item) => (
          <Col key={item.key} xs={24} sm={12} md={6}>
            <Card loading={isLoading} style={{ height: "100%" }}>
              <Text style={{ display: "block" }}>{item.label}</Text>
              <div style={{ fontSize: 28, fontWeight: 600, lineHeight: "34px", marginTop: 6 }}>
                {formatKpiValue(item.value, item.format)}
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Tabs className="dashboard-proto-tabs" defaultActiveKey="vendas" items={tabItems} />
        </Col>
      </Row>
    </div>
  );
}
